import React from 'react';
import {
  Layout,
  Card,
  Text,
  Button,
  Badge,
  BlockStack,
  InlineStack,
  InlineGrid,
  Box,
  Icon,
  Link
} from '@shopify/polaris';
import {
  ProductIcon,
  HashtagIcon,
  PersonIcon,
  GiftCardIcon,
  LockIcon,
  CheckIcon,
  DiscountIcon
} from '@shopify/polaris-icons';
import inclubWordmarkWhite from '../assets/inclubWordmarkWhite.png';

// Opens the request-access modal on the brand site (landing.js reads the hash).
const REQUEST_ACCESS_URL = 'https://www.inclub.vip/brands#request-access';

const FEATURES = [
  {
    icon: ProductIcon,
    background: 'bg-surface-info',
    tone: 'info',
    title: 'Your products, where the conversation happens',
    body: (
      <>
        Your catalogue publishes to the <Text as="span" fontWeight="semibold">InClub sales
        channel</Text> and stays in sync automatically. Collections are your call. Publish
        the ones your community should browse. Your product data is never edited.
      </>
    )
  },
  {
    icon: HashtagIcon,
    background: 'bg-surface-warning',
    tone: 'warning',
    title: 'Talk to your customers like you know them',
    body: (
      <>
        Members are tagged in your customer database, for example <code>InClub_Badge_Gold_Member</code>,{' '}
        <code>InClub_Interest_Fitness</code>, ready for segments, flows and discounts.
        Only its own <code>InClub_</code> tags, nothing else touched.
      </>
    )
  },
  {
    icon: PersonIcon,
    background: 'bg-surface-success',
    tone: 'success',
    title: 'You built the audience. You should own it',
    body: (
      <>
        Every member lands in your Shopify customer list, created if they're new. Your
        customer database stays the single source of truth.
      </>
    )
  },
  {
    icon: GiftCardIcon,
    background: 'bg-surface-magic',
    tone: 'magic',
    title: 'Every purchase brings them closer',
    body: (
      <>
        New orders unlock membership tiers and open community access automatically,
        on the spend rungs you set. No manual work.
      </>
    )
  }
];

const CONTROL_POINTS = [
  'Unpublish a product any time. Collections only appear when you choose them',
  'Your existing customer tags, profiles and marketing settings are never modified',
  'Customers are never contacted by InClub without joining your community themselves',
  'Disconnect any time. Your store data stays yours'
];

const SETUP_STEPS = [
  'Sign in to InClub',
  'Create your club, or link an existing one',
  'Pick your collections'
];

/**
 * Primary call-to-action for both CTA slots.
 * - approved store  → "Connect to InClub" (starts the linking flow)
 * - not approved    → "Contact us" (new tab to the request-access modal)
 */
function PrimaryCta({ storeApproved, onConnect, isWaitingForConnection, variant }) {
  if (!storeApproved) {
    return (
      <Button variant={variant} size="large" url={REQUEST_ACCESS_URL} external>
        Contact us
      </Button>
    );
  }
  return (
    <Button
      variant={variant}
      size="large"
      loading={isWaitingForConnection}
      disabled={isWaitingForConnection}
      onClick={onConnect}
    >
      Connect to InClub
    </Button>
  );
}

export default function ConnectLanding({ storeApproved, onConnect, isWaitingForConnection }) {
  return (
    <>
      {/* Hero: the one bold element on the page */}
      <Layout.Section>
        <div
          style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            padding: '48px 32px',
            textAlign: 'center',
            boxShadow: '0 1px 0 rgba(26,26,26,.07)'
          }}
        >
          <img
            src={inclubWordmarkWhite}
            alt="inClub"
            style={{ height: '36px', marginBottom: '20px' }}
          />
          <h1
            style={{
              color: '#ffffff',
              fontSize: '26px',
              fontWeight: 700,
              letterSpacing: '-0.3px',
              lineHeight: 1.25,
              margin: '0 auto 10px',
              maxWidth: '420px'
            }}
          >
            Build a community your customers will love.
          </h1>
          <p
            style={{
              color: 'rgba(255,255,255,0.72)',
              fontSize: '14px',
              lineHeight: 1.55,
              margin: '0 auto 24px',
              maxWidth: '440px'
            }}
          >
            A private, branded community with built-in commerce. Bring your best
            customers closer and actually own the relationship.
          </p>
          {/* Hero button stays the default (light) variant. Primary resolves to
              the theme's dark fill and disappears on the #1a1a1a hero. */}
          <PrimaryCta
            storeApproved={storeApproved}
            onConnect={onConnect}
            isWaitingForConnection={isWaitingForConnection}
          />
          <p
            style={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: '12px',
              margin: '14px 0 0'
            }}
          >
            {storeApproved
              ? 'Free to start · Works with your Shopify store'
              : 'InClub is invitation only right now. Contact us to request early access.'}
          </p>
        </div>
      </Layout.Section>

      {/* What happens when you connect */}
      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text variant="headingMd" as="h2">
                Here's exactly what happens when you connect
              </Text>
              <Text variant="bodyMd" tone="subdued">
                No surprises. InClub only touches what's listed here.
              </Text>
            </BlockStack>

            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
              {FEATURES.map(({ icon, background, tone, title, body }) => (
                <Box key={title} background="bg-surface-secondary" borderRadius="300" padding="400">
                  <BlockStack gap="200">
                    <Box background={background} borderRadius="200" padding="200" width="fit-content">
                      <Icon source={icon} tone={tone} />
                    </Box>
                    <Text variant="headingSm" as="h3">{title}</Text>
                    <Text variant="bodySm" tone="subdued">{body}</Text>
                  </BlockStack>
                </Box>
              ))}
            </InlineGrid>

            {/* Coming soon: Offers */}
            <Box borderRadius="200" padding="300" borderStyle="dashed" borderColor="border" borderWidth="025">
              <InlineStack gap="300" wrap={false} blockAlign="center">
                <Box background="bg-fill-magic-secondary" borderRadius="200" padding="200">
                  <Icon source={DiscountIcon} tone="magic" />
                </Box>
                <Box width="100%">
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="headingSm" as="h3">InClub-exclusive offers</Text>
                    <Badge tone="new">Coming soon</Badge>
                  </InlineStack>
                  <Text variant="bodySm" tone="subdued">
                    Member-only deals via hidden discount codes, applied automatically at
                    checkout, invisible to everyone else.
                  </Text>
                </Box>
              </InlineStack>
            </Box>

            {/* You stay in control */}
            <BlockStack gap="200">
              <InlineStack gap="100" blockAlign="center">
                <Box>
                  <Icon source={LockIcon} tone="base" />
                </Box>
                <Text variant="headingSm" as="h3">You stay in control</Text>
              </InlineStack>
              <BlockStack gap="100">
                {CONTROL_POINTS.map((point) => (
                  <InlineStack key={point} gap="100" wrap={false} blockAlign="start">
                    <Box>
                      <Icon source={CheckIcon} tone="success" />
                    </Box>
                    <Text variant="bodySm" tone="subdued">{point}</Text>
                  </InlineStack>
                ))}
              </BlockStack>
            </BlockStack>
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* Closing CTA with setup steps */}
      <Layout.Section>
        <Card>
          <div style={{ textAlign: 'center', padding: '24px 20px' }}>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text variant="headingMd" as="h2">
                  Your customers are already gathering. Give them a home.
                </Text>
                <Text variant="bodyMd" tone="subdued">
                  {storeApproved
                    ? "Connecting takes three steps, and you'll be guided through each one:"
                    : 'Once your store is approved, connecting takes three steps:'}
                </Text>
              </BlockStack>

              <InlineStack gap="400" align="center" blockAlign="center" wrap>
                {SETUP_STEPS.map((step, index) => (
                  <InlineStack key={step} gap="150" blockAlign="center" wrap={false}>
                    <div
                      style={{
                        flexShrink: 0,
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        background: '#1a1a1a',
                        color: '#ffffff',
                        fontSize: '11px',
                        fontWeight: 650,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {index + 1}
                    </div>
                    <Text variant="bodyMd">{step}</Text>
                  </InlineStack>
                ))}
              </InlineStack>

              <div>
                <PrimaryCta
                  storeApproved={storeApproved}
                  onConnect={onConnect}
                  isWaitingForConnection={isWaitingForConnection}
                  variant="primary"
                />
              </div>
              {!storeApproved && (
                <Text variant="bodySm" tone="subdued">
                  Contact us to request early access. We reply to every request within one business day.
                </Text>
              )}
              <Text variant="bodySm" tone="subdued">
                By connecting you agree to InClub's{' '}
                <Link url="https://inclub.vip/terms-brands" external>Terms of Service</Link>
                {' '}and{' '}
                <Link url="https://inclub.vip/privacy" external>Privacy Policy</Link>.
              </Text>
            </BlockStack>
          </div>
        </Card>
      </Layout.Section>
    </>
  );
}
